import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import type { Service } from '@planner/types'

export function useServices(includeInactive = false) {
  const [services, setServices] = useState<Service[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchServices = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      let query = supabase
        .from('services')
        .select(`
          *,
          service_category:service_categories (*)
        `)
        .order('name', { ascending: true })

      if (!includeInactive) {
        query = query.eq('is_active', true)
      }

      const { data, error: fetchError } = await query

      if (fetchError) {
        setError(fetchError.message)
      } else {
        setServices(data || [])
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Wystąpił nieoczekiwany błąd')
    } finally {
      setLoading(false)
    }
  }, [includeInactive])

  useEffect(() => {
    fetchServices()
  }, [fetchServices])

  const createService = useCallback(async (service: Omit<Service, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      const { data, error: createError } = await supabase
        .from('services')
        .insert(service)
        .select(`
          *,
          service_category:service_categories (*)
        `)
        .single()

      if (createError) {
        return { error: createError.message, data: null }
      }

      await fetchServices()
      return { error: null, data }
    } catch (err) {
      return { error: err instanceof Error ? err.message : 'Wystąpił nieoczekiwany błąd', data: null }
    }
  }, [fetchServices])

  const updateService = useCallback(async (id: string, updates: Partial<Service>) => {
    try {
      const { data, error: updateError } = await supabase
        .from('services')
        .update(updates)
        .eq('id', id)
        .select(`
          *,
          service_category:service_categories (*)
        `)
        .single()

      if (updateError) {
        return { error: updateError.message, data: null }
      }

      await fetchServices()
      return { error: null, data }
    } catch (err) {
      return { error: err instanceof Error ? err.message : 'Wystąpił nieoczekiwany błąd', data: null }
    }
  }, [fetchServices])

  const deleteService = useCallback(async (id: string) => {
    try {
      const { error: deleteError } = await supabase
        .from('services')
        .delete()
        .eq('id', id)

      if (deleteError) {
        return { error: deleteError.message }
      }

      await fetchServices()
      return { error: null }
    } catch (err) {
      return { error: err instanceof Error ? err.message : 'Wystąpił nieoczekiwany błąd' }
    }
  }, [fetchServices])

  return {
    services,
    loading,
    error,
    fetchServices,
    createService,
    updateService,
    deleteService,
  }
}


